import { BusinessObjectPart, BusinessObjectPartContext, CollectionChangedJoinPoint, PropertyChangedJoinPoint, Weaver as WeaverBase } from '@skylab-shared/core';
import { InventoryEffect, InventoryGoodsIssueDocumentModel, SaleAdvanceDocumentItemModel, SaleAdvanceDocumentModel, SaleDocumentItemModel, SaleDocumentKind, SaleDocumentModel } from '@skylab-shared/dataModel';
import { dateDiffInDays, not } from '@skylab-shared/utils';
import { withModule } from '../../availability/module-availability';
import { ContextKeys } from '../../common/context-keys';
import { isDocumentWithIncludedInventoryItems } from '../../common/document-utils';
import { propertyChangedJoinPointFilter } from '../../common/property-changed/property-changed-filter';
import { collectionChangedJoinPointFilter } from '../../utils/collection-changed-filter';
import { isAdvanceModel } from '../utils/sale-utils';
import { SaleGoodsIssueDateShouldBeEmpty, SaleGoodsIssueDateShouldNotBeEmpty } from '../violations/sale-goods-issue-date-earlier-than-adjusted-error';

export class SaleDocumentIssuedToInventoryGoodsIssueBoPart extends BusinessObjectPart<SaleDocumentModel> {
    public constructor(
        weaver: WeaverBase
    ) {
        super(weaver);
    }

    public override async initializeAsync(context: BusinessObjectPartContext<SaleDocumentModel>): Promise<void> {
        await super.initializeAsync(context);
        this.registerReadonlyProperties();
        this.synchronizeInventoryEffect();
        this.synchronizeGoodsIssueDate();
        this.validateGoodsIssueDate();
    }

    private registerReadonlyProperties(): void {
        const itemModel = isAdvanceModel(this.bo.model) ? SaleAdvanceDocumentItemModel : SaleDocumentItemModel;

        this.engine.readonly.addRule([
            [this.modelType, 'inventoryEffect'],
        ], () => this.isInvalidDocumentKindForInventoryEffect || !isDocumentWithIncludedInventoryItems(this.bo.model) || this.documentIssuedToInventoryGoodsIssue || this.advanceUnavailable);

        this.engine.readonly.addRule([
            [this.modelType, 'client'],
        ], () => (this.documentIssuedToInventoryGoodsIssue && this.bo.model.isPersistent) || !!this.relatedGoodsIssueDocument?.client);

        this.engine.readonly.addRule([
            [this.modelType, 'goodsIssueDate']
        ], () => this.documentIssuedToInventoryGoodsIssue);

        this.engine.readonly.addRule(
            [[itemModel, 'productId'], [itemModel, 'productName'], [itemModel, 'quantity'], [itemModel, 'unitId']],
            [[itemModel, 'productName'], [itemModel, 'quantity'], [itemModel, 'unitId'],
        ], ctx => this.documentIssuedToInventoryGoodsIssue && ctx.source.includesInventoryControl);
    }

    private get documentIssuedToInventoryGoodsIssue(): boolean {
        return !!this.bo.localContext.getPreloaded(ContextKeys.isInvoiceToInventoryGoodsIssue(this.bo.model.sun));
    }

    private get relatedGoodsIssueDocument(): InventoryGoodsIssueDocumentModel | null {
        if (![SaleDocumentKind.Invoice, SaleDocumentKind.Receipt].includes(this.bo.model.kind)) return null;
        return this.bo.localContext.getPreloaded(ContextKeys.relatedGoodsIssueDocumentToInvoice(this.bo.model.sun)) || null;
    }

    private synchronizeInventoryEffect(): void {
        const handle = () => {
            if (this.isInvalidDocumentKindForInventoryEffect) return;

            if (this.advanceUnavailable) {
                this.bo.model.inventoryEffect = null;
                return;
            };

            const withInventory = isDocumentWithIncludedInventoryItems(this.bo.model);
            if (this.bo.model.inventoryEffect && withInventory) return;
            if (this.bo.model.inventoryEffect && !withInventory) this.bo.model.inventoryEffect = null;
            if (!this.bo.model.inventoryEffect && withInventory) this.bo.model.inventoryEffect = withModule('Inventory') ? InventoryEffect.Realized : InventoryEffect.NotRealized;
        };

        this.registerListening(this.weaver.register(CollectionChangedJoinPoint, collectionChangedJoinPointFilter(this.bo)(this.bo.model.items)(
            _ => handle()
        )));

        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this.bo)(SaleDocumentItemModel, ['includesInventoryControl'])(
            _ => handle()
        )));

        if (isAdvanceModel(this.bo.model)) {
            this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this.bo)(SaleAdvanceDocumentModel, ['saleDate'])(
                _ => handle()
            )));
        }
    }

    private synchronizeGoodsIssueDate(): void {
        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this.bo)(this.modelType, ['inventoryEffect'])(
            ctx => {
                if (this.bo.isPropertyReadonly(this.bo.model, 'goodsIssueDate')) return;

                if (!ctx.oldValue) {
                    this.bo.model.goodsIssueDate = this.bo.model.saleDate ? new Date(this.bo.model.saleDate) : null;
                } else if (this.bo.model.inventoryEffect === null) {
                    this.bo.model.goodsIssueDate = null;
                }
            }
        )));

        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this.bo)(this.modelType, ['saleDate'])(
            ctx => {
                if (this.bo.isPropertyReadonly(this.bo.model, 'goodsIssueDate')) return;
                if (!this.bo.model.inventoryEffect || !this.bo.model.saleDate) return;

                if (!this.bo.model.goodsIssueDate || !dateDiffInDays(ctx.oldValue, this.bo.model.goodsIssueDate)) {
                    this.bo.model.goodsIssueDate = new Date(this.bo.model.saleDate);
                }
            }
        )));
    }

    private validateGoodsIssueDate(): void {
        this.engine.validation.addRule(
            [[this.modelType, 'goodsIssueDate'], [this.modelType, 'inventoryEffect']],
            [[this.modelType, 'goodsIssueDate']],
            ctx => {
                return not(!this.bo.model.inventoryEffect && this.bo.model.goodsIssueDate)
            },
            SaleGoodsIssueDateShouldBeEmpty
        );

        this.engine.validation.addRule(
            [[this.modelType, 'goodsIssueDate'], [this.modelType, 'inventoryEffect']],
            [[this.modelType, 'goodsIssueDate']],
            ctx => {
                return not(this.bo.model.inventoryEffect && !this.bo.model.goodsIssueDate)
            },
            SaleGoodsIssueDateShouldNotBeEmpty
        );
    }

    private get isInvalidDocumentKindForInventoryEffect(): boolean {
        return [SaleDocumentKind.VatMarginInvoice, SaleDocumentKind.VatMarginInvoiceAdjustment].includes(this.bo.model.kind);
    }

    private get advanceUnavailable(): boolean {
        return isAdvanceModel(this.bo.model) && !this.bo.model.saleDate;
    }
}
